import { AnimatePresence, motion } from 'framer-motion';
import React from 'react';
import type { BgmSoundType } from '../../hooks/useReaderSettings';

interface AudioSettingsSectionProps {
  soundVolume: number;
  setSoundVolume: (v: number) => void;
  bgmEnabled: boolean;
  setBgmEnabled: (v: boolean) => void;
  bgmVolume: number;
  setBgmVolume: (v: number) => void;
  bgmSound: BgmSoundType;
  setBgmSound: (s: BgmSoundType) => void;
  activeBtnBg: string;
  inactiveBtnBg: string;
}

export const AudioSettingsSection: React.FC<AudioSettingsSectionProps> = React.memo(
  ({
    soundVolume,
    setSoundVolume,
    bgmEnabled,
    setBgmEnabled,
    bgmVolume,
    setBgmVolume,
    bgmSound,
    setBgmSound,
    activeBtnBg,
    inactiveBtnBg,
  }) => {
    return (
      <>
        {/* ページめくり効果音 */}
        <div className="space-y-3.5 p-3 rounded-md border border-neutral-500/10">
          <div className="space-y-1">
            <div className="flex justify-between text-[11px] font-bold">
              <span>ページめくり効果音</span>
              <span className="font-mono">{Math.round(soundVolume * 100)}%</span>
            </div>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={soundVolume}
              onChange={(e) => setSoundVolume(Number(e.target.value))}
              className="w-full accent-neutral-850 dark:accent-neutral-200 h-11"
            />
          </div>
        </div>

        {/* 環境音BGM */}
        <div className="space-y-3.5 p-3 rounded-md border border-neutral-500/10">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <div className="text-xs font-bold">環境音BGM</div>
              <div className="text-[10px] opacity-40">読書中に穏やかな自然音をループ再生</div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer select-none">
              <input
                type="checkbox"
                checked={bgmEnabled}
                onChange={(e) => setBgmEnabled(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-9 h-5 bg-neutral-300 dark:bg-neutral-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-neutral-850 dark:peer-checked:bg-neutral-100" />
            </label>
          </div>

          <AnimatePresence initial={false}>
            {bgmEnabled && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="space-y-3 pt-2 border-t border-current/5 mt-2 overflow-hidden"
              >
                {/* 音源選択 */}
                <div className="space-y-1">
                  <span className="text-[10px] font-bold opacity-50 block">音源</span>
                  <div className="grid grid-cols-3 gap-2">
                    {(
                      [
                        { id: 'rain', label: '雨音' },
                        { id: 'cafe', label: 'カフェ' },
                        { id: 'wave', label: '波音' },
                      ] as const
                    ).map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => setBgmSound(s.id)}
                        className={`min-h-11 rounded text-xs font-bold px-1 transition-all ${
                          bgmSound === s.id ? activeBtnBg : inactiveBtnBg
                        }`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* BGM 音量 */}
                <div className="space-y-1">
                  <div className="flex justify-between text-[11px] font-bold">
                    <span>音量</span>
                    <span className="font-mono">{Math.round(bgmVolume * 100)}%</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={bgmVolume}
                    onChange={(e) => setBgmVolume(Number(e.target.value))}
                    className="w-full accent-neutral-850 dark:accent-neutral-200 h-11"
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </>
    );
  },
);

AudioSettingsSection.displayName = 'AudioSettingsSection';
